import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import { VictoryArea, VictoryAxis, VictoryChart, VictoryLine, VictoryScatter } from 'victory';
import dayjs from 'dayjs';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import useElementSize from 'common/hooks/useElementSize';
import Logger from 'common/services/Logger';
import Storage from 'common/services/Storage';
import Utils from 'common/services/Utils';
import { LOGGER_LOG_TYPE, DEFAULT_LANGUAGE, STORAGE } from 'Config';
import { ReactComponent as EcoIcon } from 'assets/svg/eco-conducao.svg';
import { EcoEvolutionPointDashboardDto } from 'api/dashboard/dtos/EcoEvolutionPointDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import WidgetNoData from '../components/WidgetNoData';
import {
	ZONE_SAFE_MAX, ZONE_WARNING_MAX, ECO_SAFE_COLOR, ECO_WARNING_COLOR, ECO_DANGER_COLOR,
	calcEcoColor, normalizeMonthDate, toMonthKey,
} from '../utils/ecoShared';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import styles from './EcoEvolution.module.scss';

// Linha verde-escura para destacar sobre a banda verde-clara do fundo.
const LINE_COLOR = '#2E9E4F';

type ChartPoint = { x: Date; y: number };

type TooltipState = { monthKey: string; date: Date; value: number; x: number; y: number };

type Props = {
	filters?: FiltersFormModel;
};

const EcoEvolution = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<EcoEvolutionPointDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [tooltip, setTooltip] = useState<TooltipState | null>(null);
	const [chartRef, { width, height }] = useElementSize();
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('ecoEvolution');
	const scopeVehicleId = useDashboardVehicleScope();
	const vehicleScope = vehicleScopeCriteria(scopeVehicleId);

	const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters ? { ...props.filters } : { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getEcoEvolution(criteria);
				if (!cancelled) setData(result || []);
			} catch (error) {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error getting eco evolution', error);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const points = useMemo<ChartPoint[]>(() =>
		data
			.map((p): ChartPoint | null => p.ecoValue == null ? null : { x: normalizeMonthDate(p.month), y: Number(p.ecoValue) })
			.filter((p): p is ChartPoint => p != null),
		[data]);

	const allValues = useMemo(() => points.map(p => p.y), [points]);
	const maxY = useMemo(() => {
		const dataMax = allValues.length > 0 ? Math.max(...allValues) : 0;
		// Garante que a banda vermelha (>=15) é sempre visível mesmo sem valores nessa zona.
		return Math.max(dataMax * 1.1, ZONE_WARNING_MAX + 5);
	}, [allValues]);

	const xDomain = useMemo<[Date, Date]>(() => {
		const all = data.map(p => normalizeMonthDate(p.month).getTime());
		if (all.length > 0) return [new Date(Math.min(...all)), new Date(Math.max(...all))];
		return [dateFrom, dateTo];
	}, [data, dateFrom, dateTo]);

	const tickValues = useMemo(() => {
		const seen = new Set<number>();
		const dates: Date[] = [];
		data.forEach(p => {
			const date = normalizeMonthDate(p.month);
			if (!seen.has(date.getTime())) { seen.add(date.getTime()); dates.push(date); }
		});
		return dates.sort((a, b) => a.getTime() - b.getTime());
	}, [data]);

	const formatMonth = (date: Date) =>
		Utils.capitalize(dayjs(date).locale(locale).format('MMM')) + ' ' + dayjs(date).format('YY');

	const scatterEvents = [{
		target: 'data' as const,
		eventHandlers: {
			onMouseEnter: (evt: any, targetProps: any) => {
				const d = targetProps.datum as ChartPoint;
				setTooltip({ monthKey: toMonthKey(d.x), date: d.x, value: d.y, x: evt.clientX, y: evt.clientY });
				return [];
			},
			onMouseMove: (evt: any) => {
				setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
				return [];
			},
			onMouseLeave: () => { setTooltip(null); return []; },
		},
	}];

	const renderChart = () => {
		if (isLoading) {
			return <div className={styles.centerAlign}><LoadingSpinner /></div>;
		}
		if (points.length === 0) {
			return <WidgetNoData />;
		}
		if (width === 0 || height === 0) return null;

		const [xMin, xMax] = xDomain;
		const safeBand = [
			{ x: xMin, y0: 0, y: ZONE_SAFE_MAX },
			{ x: xMax, y0: 0, y: ZONE_SAFE_MAX },
		];
		const warningBand = [
			{ x: xMin, y0: ZONE_SAFE_MAX, y: ZONE_WARNING_MAX },
			{ x: xMax, y0: ZONE_SAFE_MAX, y: ZONE_WARNING_MAX },
		];
		const dangerBand = [
			{ x: xMin, y0: ZONE_WARNING_MAX, y: maxY },
			{ x: xMax, y0: ZONE_WARNING_MAX, y: maxY },
		];

		return (
			<VictoryChart
				width={width}
				height={height}
				scale={{ x: 'time' }}
				padding={{ top: 15, bottom: 45, left: 50, right: 20 }}
				domain={{ y: [0, maxY] }}
				domainPadding={{ x: 25 }}
			>
				<VictoryArea data={safeBand} interpolation="linear" style={{ data: { fill: ECO_SAFE_COLOR, fillOpacity: 0.13, stroke: 'transparent' } }} />
				<VictoryArea data={warningBand} interpolation="linear" style={{ data: { fill: ECO_WARNING_COLOR, fillOpacity: 0.13, stroke: 'transparent' } }} />
				<VictoryArea data={dangerBand} interpolation="linear" style={{ data: { fill: ECO_DANGER_COLOR, fillOpacity: 0.13, stroke: 'transparent' } }} />
				<VictoryAxis
					tickValues={tickValues}
					tickFormat={(d: any) => formatMonth(new Date(d))}
					style={{
						axis: { stroke: 'transparent' },
						tickLabels: { fontSize: 11, fontFamily: 'Roboto', fill: '#6b7280' },
					}}
				/>
				<VictoryAxis
					dependentAxis
					style={{
						axis: { stroke: 'transparent' },
						grid: { stroke: '#f5f5f5' },
						tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
					}}
				/>
				<VictoryLine
					data={points}
					x="x"
					y="y"
					interpolation="monotoneX"
					style={{ data: { stroke: LINE_COLOR, strokeWidth: 2 } }}
				/>
				<VictoryScatter
					data={points}
					x="x"
					y="y"
					size={4}
					style={{ data: { fill: LINE_COLOR, cursor: 'pointer' } }}
					// eslint-disable-next-line
					events={scatterEvents}
				/>
			</VictoryChart>
		);
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.eco_evolution' as any) as string}
			subTitleTooltip={t((scopeVehicleId ? 'dashboard.eco_evolution_asset_tooltip' : 'dashboard.eco_evolution_tooltip') as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			<div className={styles.chartContainer}>
				<div className={styles.chartArea} ref={chartRef}>
					{renderChart()}
					{tooltip && (
						<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
							<div className={styles.tooltipTitle}>{formatMonth(tooltip.date)}</div>
							<div className={styles.tooltipRow}>
								<span className={styles.tooltipRowLabel}>{t('dashboard.eco_evolution_penalty' as any) as string}</span>
								<span className={styles.tooltipRowValue} style={{ color: calcEcoColor(tooltip.value) }}>
									{tooltip.value.toFixed(2)}
								</span>
							</div>
						</ChartTooltipPortal>
					)}
				</div>
				{points.length > 0 && (
					<>
						<div
							className={styles.zonesLegend}
							data-tip={t('location.eco_penalty_label' as any) as string}
							data-for="ecoEvolutionZonesTip"
						>
							<span className={styles.zonesLegendItem}>
								<EcoIcon className={styles.zonesLegendIcon} fill={ECO_SAFE_COLOR} />
								{t('location.tooltip_eco_success', { until: 5 }) as string}
							</span>
							<span className={styles.zonesLegendItem}>
								<EcoIcon className={styles.zonesLegendIcon} fill={ECO_WARNING_COLOR} />
								{t('location.tooltip_eco_warning', { from: 5.01, until: 14.99 }) as string}
							</span>
							<span className={styles.zonesLegendItem}>
								<EcoIcon className={styles.zonesLegendIcon} fill={ECO_DANGER_COLOR} />
								{t('location.tooltip_eco_danger', { from: 15 }) as string}
							</span>
						</div>
						<ReactTooltip id="ecoEvolutionZonesTip" effect="solid" />
					</>
				)}
			</div>
		</Box>
	);
};

export default EcoEvolution;
